import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { intlLocaleFor } from './intlLocale';
import {
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  loadInitialLanguage,
  persistLanguage,
  type SupportedLanguage,
} from './languagePersistence';
import deCommon from './locales/de/common.json';
import enCommon from './locales/en/common.json';
import frCommon from './locales/fr/common.json';

export type { SupportedLanguage };

export const defaultNS = 'common';
export const resources = {
  de: { common: deCommon },
  en: { common: enCommon },
  fr: { common: frCommon },
} as const;

/** Swiss Standard German (Schweizer Hochdeutsch) — default UI language. */
export const SUPPORTED_LANGUAGES: readonly {
  code: SupportedLanguage;
  nativeLabel: string;
}[] = [
  { code: 'de', nativeLabel: 'Deutsch' },
  { code: 'fr', nativeLabel: 'Français' },
  { code: 'en', nativeLabel: 'English' },
];

const initialLanguage = loadInitialLanguage();

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: ['de', 'fr', 'en'],
  /** Map browser locales such as `de-CH` to `de`. */
  nonExplicitSupportedLngs: true,
  defaultNS,
  interpolation: { escapeValue: false },
});

const syncDocumentLang = (language: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = intlLocaleFor(language);
  }
};

const onLanguageChanged = (language: string) => {
  const code = language.split('-')[0];
  if (isSupportedLanguage(code)) {
    persistLanguage(code);
  }
  syncDocumentLang(language);
};

syncDocumentLang(i18n.language);
i18n.on('languageChanged', onLanguageChanged);

export default i18n;
